import { CirclePause, Clock, Play, RotateCcw } from '@orgus/ui/icons';
import { isStale, pipelineKey } from '@orgus/protocol';
import type { ActionKind, Machine, Pipeline, Project } from '@orgus/protocol';
import {
  Button,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  ProjectMark,
  Select,
  StatusPill,
  Tooltip,
  formatDuration,
  machineLabel,
  machineTone,
  pipelineLabel,
  plural,
  useNow,
} from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';
import { TabMenu } from '../landing/PipelinesTabs.tsx';

export type PipelineAction = Extract<ActionKind, 'pipeline.pause' | 'pipeline.resume' | 'pipeline.restart'>;

/** Orgus only watches a project: a pipeline action is the one place it changes the project's own queue. */
const WRITES = 'Orgus otherwise only reads this project; this changes its own queue on that machine.';

/** The consequence of each pipeline action, in words, for the confirm dialog. */
export function actionCopy(kind: PipelineAction, p: Pipeline, machine: string): { title: string; description: string; confirm: string } {
  const active = p.items.filter((i) => i.state === 'active');
  const queued = p.items.filter((i) => i.state === 'queued').length;
  const name = p.title.toLowerCase();
  if (kind === 'pipeline.pause') {
    return {
      title: `Pause the ${name} on ${machine}?`,
      description: `Nothing new starts until you resume it.${active.length ? ` ${active.length === 1 ? 'The item in progress finishes' : `The ${active.length} items in progress finish`} first.` : ''}${queued ? ` ${plural(queued, 'queued item')} wait${queued === 1 ? 's' : ''} in place.` : ''} ${WRITES}`,
      confirm: `Pause ${name}`,
    };
  }
  if (kind === 'pipeline.resume') {
    return {
      title: `Resume the ${name} on ${machine}?`,
      description: `${queued ? `It starts the next of ${plural(queued, 'queued item')} right away.` : 'It picks up new items as they arrive.'} ${WRITES}`,
      confirm: `Resume ${name}`,
    };
  }
  return {
    title: `Restart the ${name} on ${machine}?`,
    description: `Its runner stops and starts again.${active.length ? ` Work in progress (${active.map((a) => `"${a.title}"`).join(', ')}) is interrupted and runs again.` : ''} Queued items keep their places. ${WRITES}`,
    confirm: `Restart ${name}`,
  };
}

const ICON: Record<PipelineAction, typeof Play> = { 'pipeline.pause': CirclePause, 'pipeline.resume': Play, 'pipeline.restart': RotateCcw };
/** Menu items open a confirm dialog, so they end in an ellipsis (components.md). */
const ITEM_LABEL: Record<PipelineAction, (name: string) => string> = {
  'pipeline.pause': (n) => `Pause the ${n}…`,
  'pipeline.resume': (n) => `Resume the ${n}…`,
  'pipeline.restart': (n) => `Restart the ${n}…`,
};

const PILL: Record<Pipeline['state'], PillStatus> = { running: 'working', idle: 'idle', paused: 'paused', down: 'offline', error: 'failed' };

/** Which pipeline actions apply right now, given the adapter's list and the state. */
export function availableActions(p: Pipeline): PipelineAction[] {
  const out: PipelineAction[] = [];
  if (p.actions.includes('pipeline.pause') && p.state !== 'paused') out.push('pipeline.pause');
  if (p.actions.includes('pipeline.resume') && p.state === 'paused') out.push('pipeline.resume');
  if (p.actions.includes('pipeline.restart')) out.push('pipeline.restart');
  return out;
}

/**
 * The pipeline's controls in the page's one header row (pipelines.md 2.1), at the right of the
 * view tabs: how long ago it last reported, only when it has gone quiet; its state as one soft
 * status; which pipeline this is (a picker with two or more, the name with one, each naming
 * project and machine); and Pause or Resume with Restart behind the overflow. That Orgus only
 * reads the project is said in that menu, where it matters.
 */
export function PipelineHeader({
  pipeline: p,
  machine,
  project,
  onAction,
  pipelines,
  projectName,
  machineName,
  onSelect,
  actions: boundActions,
  notReporting,
}: {
  pipeline: Pipeline;
  machine: Machine | undefined;
  project: Project | undefined;
  onAction: (kind: PipelineAction) => void;
  /** Every pipeline, for the picker (shown with two or more). */
  pipelines?: Pipeline[];
  projectName?: (id: string) => string;
  /** Names the machine in each picker option, so two queues of one project never read the same. */
  machineName?: (id: string) => string;
  onSelect?: (key: string) => void;
  /** A protocol pipeline's actions: the project's declared actions bound to these roles. */
  actions?: PipelineAction[];
  /** A protocol pipeline whose pipeline.json stopped reporting: "Not reporting since 8:41 PM". */
  notReporting?: string | null;
}) {
  const now = useNow(5000);
  const stale = !notReporting && isStale(p.updatedAt, 'pipeline', now);
  const machineOk = machine ? machineTone(machine, now) === 'passed' : false;
  const actions = boundActions ?? availableActions(p);
  const blockedWhy = !machine ? 'Its machine is no longer registered.' : !machineOk ? `${machine.name} is ${machineLabel(machine, now).toLowerCase()}, so it cannot act.` : null;
  const many = pipelines && pipelines.length > 1 && onSelect;
  const name = project?.name ?? 'Unknown project';
  const where = machine?.name ?? 'an unknown machine';

  return (
    <>
      {stale && (
        <Tooltip content="The machine has not reported this pipeline recently; what you see may be out of date.">
          <span tabIndex={0} className="flex shrink-0 items-center gap-1 rounded-control text-caption text-fg-subtle num">
            <Clock className="size-3 shrink-0 text-status-attention" aria-hidden />
            Not updated for {staleFor(now - p.updatedAt)}
          </span>
        </Tooltip>
      )}
      {notReporting ? <StatusPill status="offline" label={notReporting} /> : <StatusPill status={PILL[p.state]} label={pipelineLabel(p)} />}
      {many ? (
        <Select
          size="sm"
          aria-label="Pipeline"
          value={pipelineKey(p)}
          onValueChange={onSelect}
          options={pipelines.map((x) => ({
            value: pipelineKey(x),
            // One pattern for every option, "<project> on <machine>"; the title only tells two apart that would read the same.
            label: pipelineOptionLabel(x, projectName, machineName, pipelines.some((y) => y !== x && y.projectId === x.projectId && y.machineId === x.machineId)),
            icon: <ProjectMark projectId={x.projectId} name={projectName?.(x.projectId) ?? ''} size="xs" />,
          }))}
          wrapperClassName="w-64 max-w-full max-sm:order-first max-sm:w-full"
        />
      ) : (
        <span className="flex min-w-0 items-center gap-2 text-ui" title={machine?.hostname}>
          <ProjectMark projectId={p.projectId} name={name} size="xs" />
          <span className="min-w-0 truncate font-medium text-fg">
            {name} <span className="font-normal text-fg-muted">{p.title.toLowerCase()}</span>
          </span>
          <span className="shrink-0 text-caption text-fg-subtle">on {where}</span>
        </span>
      )}
      {actions.length > 0 && <QueueControls pipeline={p} project={project} actions={actions} blockedWhy={blockedWhy} onAction={onAction} />}
    </>
  );
}

/** "37m" past a minute, "42s" under one: how long a report is overdue, never to the second. */
function staleFor(ms: number): string {
  return formatDuration(ms < 60_000 ? Math.max(1000, Math.floor(ms / 1000) * 1000) : Math.floor(ms / 60_000) * 60_000);
}

/**
 * "Qawl-Agents on studio": one picker option in the one pattern the Pipelines pickers share. The
 * title joins in ("Qawl-Agents landing queue on studio") only where a project runs two pipelines
 * on one machine, so no two options read the same.
 */
export function pipelineOptionLabel(
  p: Pick<Pipeline, 'projectId' | 'machineId' | 'title'>,
  projectName?: (id: string) => string,
  machineName?: (id: string) => string,
  withTitle = false,
): string {
  const base = `${projectName?.(p.projectId) ?? 'Unknown project'}${withTitle ? ` ${p.title.toLowerCase()}` : ''}`;
  return machineName ? `${base} on ${machineName(p.machineId)}` : base;
}

/**
 * Pause or Resume as one button, Restart behind the overflow menu that says what they touch:
 * Orgus watches the project read-only, and these are the only actions that change the
 * project's own queue. Each opens its confirmation first.
 */
function QueueControls({
  pipeline: p,
  project,
  actions,
  blockedWhy,
  onAction,
}: {
  pipeline: Pipeline;
  project: Project | undefined;
  actions: PipelineAction[];
  blockedWhy: string | null;
  onAction: (kind: PipelineAction) => void;
}) {
  const name = p.title.toLowerCase();
  const toggle = actions.find((k) => k === 'pipeline.pause' || k === 'pipeline.resume');
  const restart = actions.includes('pipeline.restart');
  const ToggleIcon = toggle ? ICON[toggle] : null;
  const body = (
    <span className="flex items-center gap-1">
      {toggle && ToggleIcon && (
        <Button size="sm" variant="secondary" icon={<ToggleIcon />} disabled={!!blockedWhy} onClick={() => onAction(toggle)}>
          {toggle === 'pipeline.pause' ? 'Pause' : 'Resume'}
        </Button>
      )}
      {restart && (
        <TabMenu
          label={`More ${name} controls`}
          header={
            <>
              <DropdownMenuLabel className="whitespace-normal">
                Orgus only watches {project?.name ?? 'this project'}. These change its own {name}.
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          }
          items={[{ label: ITEM_LABEL['pipeline.restart'](name), icon: <RotateCcw />, danger: true, onSelect: () => onAction('pipeline.restart') }]}
          disabled={!!blockedWhy}
        />
      )}
    </span>
  );
  if (blockedWhy) {
    return (
      <Tooltip content={blockedWhy}>
        <span tabIndex={0}>{body}</span>
      </Tooltip>
    );
  }
  return body;
}
